import React from 'react'
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image'

export type ImageProps = {
  alt?: string
  isDecorative?: boolean
  classname?: string
  height?: number
  id?: string
  src?: string
  width?: number
  gatsbyImageData?: IGatsbyImageData
}

/**
 * Determines the appropriate alt attribute value.
 * - Decorative images: returns "" (empty string) to hide from screen readers
 * - Images with alt text: returns the alt text
 * - Missing alt text: returns undefined to fail accessibility tests
 */
function getAltAttribute(
  alt?: string,
  isDecorative = false
): string | undefined {
  // If explicitly decorative, always return empty string
  if (isDecorative === true) {
    return ''
  }

  // If has meaningful alt text, return it
  if (alt && alt.trim() !== '') {
    return alt
  }

  // No alt text and not decorative = omit attribute
  return undefined
}

export const Image = ({
  alt = '',
  isDecorative,
  classname,
  height,
  id,
  src,
  width,
  gatsbyImageData
}: ImageProps) => {
  const altAttribute = getAltAttribute(alt, isDecorative)

  // render image with gatsby image data
  const imageData = getImage(gatsbyImageData)
  if (imageData) {
    return (
      <GatsbyImage
        image={imageData as IGatsbyImageData}
        alt={altAttribute || ''}
        className={classname}
      />
    )
  }

  // fallback to regular img tag
  return (
    <img
      id={id}
      src={src}
      alt={altAttribute}
      className={classname}
      width={width}
      height={height}
    />
  )
}

export default Image
